import React, { useState, useEffect } from 'react';
import api, { BASE_URL, coursesAPI } from '../api';
import { toast } from 'react-toastify';
import './CoursesManager.css';

const CoursesManager = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    title: '',
    category: 'programming',
    duration: '',
    level: '',
    projects: 0,
    internship: '',
    description: '',
    detailedDescription: '',
    features: [],
    syllabus: [], // Now an array of module objects with module and content
    price: 0,
    isActive: true,
    order: 0
  });
  const [featuresInput, setFeaturesInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await coursesAPI.getAllAdmin();
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout. Make sure backend is running on port 5000');
      } else if (!error.response) {
        toast.error('Cannot connect to backend. Please check if backend server is running.');
      } else if (error.response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.reload();
      } else {
        toast.error(error.response?.data?.message || 'Failed to fetch courses');
      }
    } finally {
      setLoading(false);
    }
  };

  // Syllabus management functions - Simplified
  const addSyllabusModule = () => {
    setFormData({
      ...formData,
      syllabus: [
        ...formData.syllabus,
        {
          module: '',
          content: '' // Single content field instead of topics array
        }
      ]
    });
  };

  const updateSyllabusModule = (index, field, value) => {
    const updatedSyllabus = [...formData.syllabus];
    updatedSyllabus[index][field] = value;
    setFormData({ ...formData, syllabus: updatedSyllabus });
  };

  const removeSyllabusModule = (index) => {
    const updatedSyllabus = formData.syllabus.filter((_, i) => i !== index);
    setFormData({ ...formData, syllabus: updatedSyllabus });
  };

  const handleOpenModal = (course = null) => {
    if (course) {
      setEditingCourse(course);
      // Ensure syllabus has the correct simplified structure
      const syllabus = course.syllabus || [];
      setFormData({
        title: course.title,
        category: course.category,
        duration: course.duration,
        level: course.level,
        projects: course.projects,
        internship: course.internship,
        description: course.description,
        detailedDescription: course.detailedDescription,
        features: course.features || [],
        syllabus: syllabus,
        price: course.price || 0,
        isActive: course.isActive,
        order: course.order || 0
      });
      setFeaturesInput(course.features?.join(', ') || '');
      setImagePreview(course.imageUrl ? `${BASE_URL}${course.imageUrl}` : '');
    } else {
      setEditingCourse(null);
      setFormData({
        title: '',
        category: 'programming',
        duration: '',
        level: '',
        projects: 0,
        internship: '',
        description: '',
        detailedDescription: '',
        features: [],
        syllabus: [],
        price: 0,
        isActive: true,
        order: 0
      });
      setFeaturesInput('');
      setImageFile(null);
      setImagePreview('');
    }
    setActiveTab('basic');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCourse(null);
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    });
  };

  const handleFeaturesChange = (e) => {
    setFeaturesInput(e.target.value);
    const features = e.target.value.split(',').map(f => f.trim()).filter(f => f);
    setFormData({ ...formData, features });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate syllabus data - Simplified
    const validatedSyllabus = formData.syllabus.map(module => ({
      module: module.module || '',
      content: module.content || ''
    })).filter(module => module.module); // Only keep modules with a title
    
    const submitData = {
      ...formData,
      syllabus: validatedSyllabus,
      projects: parseInt(formData.projects) || 0,
      price: parseInt(formData.price) || 0,
      order: parseInt(formData.order) || 0
    };
    
    console.log('Submitting data:', JSON.stringify(submitData, null, 2));
    
    try {
      let courseId;
      if (editingCourse) {
        await coursesAPI.update(editingCourse._id, submitData);
        courseId = editingCourse._id;
        toast.success('Course updated successfully');
      } else {
        const response = await coursesAPI.create(submitData);
        courseId = response.data._id;
        toast.success('Course created successfully');
      }
      
      if (imageFile) {
        await coursesAPI.uploadImage(courseId, imageFile);
        toast.success('Image uploaded successfully');
      }
      
      handleCloseModal();
      fetchCourses();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || error.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await coursesAPI.delete(id);
        toast.success('Course deleted successfully');
        fetchCourses();
      } catch (error) {
        toast.error('Failed to delete course');
      }
    }
  };

  const categoryOptions = [
    { value: 'programming', label: 'Programming' },
    { value: 'design', label: 'Design' },
    { value: 'sap', label: 'SAP Modules' },
    { value: 'data', label: 'Data Science' }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      programming: '#3b82f6',
      design: '#8b5cf6',
      sap: '#10b981',
      data: '#f59e0b'
    };
    return colors[category] || '#6b7280';
  };

  const renderSyllabusEditor = () => {
    return (
      <div className="syllabus-editor">
        <div className="syllabus-header">
          <h3>Course Syllabus</h3>
          <button type="button" className="btn-add-module" onClick={addSyllabusModule}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Module
          </button>
        </div>

        {formData.syllabus.length === 0 && (
          <div className="empty-syllabus">
            <p>No modules added yet. Click "Add Module" to start building your syllabus.</p>
          </div>
        )}

        {formData.syllabus.map((module, moduleIndex) => (
          <div key={moduleIndex} className="syllabus-module">
            <div className="module-header">
              <div className="module-title-input">
                <input
                  type="text"
                  placeholder="Module Title (e.g., Module 1: Introduction to Programming)"
                  value={module.module || ''}
                  onChange={(e) => updateSyllabusModule(moduleIndex, 'module', e.target.value)}
                  className="module-title-field"
                />
              </div>
              <button
                type="button"
                className="btn-remove-module"
                onClick={() => removeSyllabusModule(moduleIndex)}
              >
                Remove
              </button>
            </div>

            {/* Module Content - Single textarea instead of topics array */}
            <div className="module-content">
              <label>Module Content:</label>
              <textarea
                placeholder="Enter module content, topics, and description here..."
                value={module.content || ''}
                onChange={(e) => updateSyllabusModule(moduleIndex, 'content', e.target.value)}
                rows="4"
                className="module-content-field"
              />
              <small className="form-hint">You can include topics, descriptions, learning objectives, etc.</small>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading courses...</p>
      </div>
    );
  }

  return (
    <div className="courses-manager">
      <div className="manager-header">
        <div className="header-content">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          <div>
            <h1>Course Management</h1>
            <p className="header-subtitle">Manage your educational content</p>
          </div>
        </div>
        <button className="btn-add" onClick={() => handleOpenModal()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add New Course
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <h3>{courses.length}</h3>
            <p>Total Courses</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <h3>{courses.filter(c => c.isActive).length}</h3>
            <p>Active Courses</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{categoryOptions.length}</h3>
            <p>Categories</p>
          </div>
        </div>
      </div>

<div className="table-wrapper">
  <table className="courses-table">
    <thead>
      <tr>
        <th>Image</th>
        <th>Title</th>
        <th>Category</th>
        <th>Duration</th>
        <th>Projects</th>
        <th>Modules</th>
        <th>Status</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {courses.map((course) => (
        <tr key={course._id}>
          <td className="image-cell">
            {course.imageUrl ? (
              <img 
                src={`${BASE_URL}${course.imageUrl}`} 
                alt={course.title}
                className="course-image"
              />
            ) : (
              <div className="image-placeholder">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15L16 10L5 21"/>
                </svg>
              </div>
            )}
          </td>
          <td className="title-cell">
            <div className="title-wrapper">
              <strong>{course.title}</strong>
              {course.price > 0 && (
                <span className="price-tag">₹{course.price.toLocaleString()}</span>
              )}
            </div>
          </td>
          <td>
            <span 
              className="category-badge"
              style={{ 
                backgroundColor: `${getCategoryColor(course.category)}20`, 
                color: getCategoryColor(course.category) 
              }}
            >
              {categoryOptions.find(c => c.value === course.category)?.label}
            </span>
          </td>
          <td className="duration-cell">
            <div className="duration-wrapper">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>{course.duration}</span>
            </div>
          </td>
          <td className="projects-cell">
            <div className="projects-wrapper">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>{course.projects} Projects</span>
            </div>
          </td>
          <td className="modules-cell">
            <span className="modules-count">
              {course.syllabus?.length || 0} Modules
            </span>
          </td>
          <td>
            <span className={`status-badge ${course.isActive ? 'status-active' : 'status-inactive'}`}>
              <span className="status-dot"></span>
              {course.isActive ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td>
            <div className="action-buttons">
              <button className="action-btn edit-btn" onClick={() => handleOpenModal(course)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                  <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                </svg>
                Edit
              </button>
              <button className="action-btn delete-btn" onClick={() => handleDelete(course._id)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete
              </button>
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  {editingCourse ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                      <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  )}
                </div>
                <div>
                  <h2>{editingCourse ? 'Edit Course' : 'Create New Course'}</h2>
                  <p className="modal-subtitle">
                    {editingCourse ? 'Update course information' : 'Add a new course to the catalog'}
                  </p>
                </div>
              </div>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-tabs">
              <button 
                type="button"
                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                Basic Information
              </button>
              <button 
                type="button"
                className={`tab-btn ${activeTab === 'syllabus' ? 'active' : ''}`}
                onClick={() => setActiveTab('syllabus')}
              >
                Syllabus Builder
              </button>
              <button 
                type="button"
                className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
                onClick={() => setActiveTab('features')}
              >
                Features & Media
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {activeTab === 'basic' && (
                <div className="tab-content">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Course Title *</label>
                      <input 
                        type="text" 
                        name="title" 
                        value={formData.title} 
                        onChange={handleInputChange} 
                        placeholder="e.g., Full Stack Development"
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Category *</label>
                      <select name="category" value={formData.category} onChange={handleInputChange} required>
                        {categoryOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Duration *</label>
                      <input 
                        type="text" 
                        name="duration" 
                        value={formData.duration} 
                        onChange={handleInputChange} 
                        placeholder="e.g., 6 months"
                        required 
                      />
                    </div>

                    <div className="form-group">
                      <label>Level *</label>
                      <input 
                        type="text" 
                        name="level" 
                        value={formData.level} 
                        onChange={handleInputChange} 
                        placeholder="e.g., Intermediate to Advanced"
                        required 
                      />
                    </div>

                    <div className="form-group">
                      <label>Projects Count *</label>
                      <input 
                        type="number" 
                        name="projects" 
                        value={formData.projects} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>

                    <div className="form-group">
                      <label>Internship Duration *</label>
                      <input 
                        type="text" 
                        name="internship" 
                        value={formData.internship} 
                        onChange={handleInputChange} 
                        placeholder="e.g., 3 Months"
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Short Description *</label>
                    <textarea 
                      name="description" 
                      value={formData.description} 
                      onChange={handleInputChange} 
                      rows="2"
                      placeholder="Brief overview of the course"
                      required 
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Detailed Description *</label>
                    <textarea 
                      name="detailedDescription" 
                      value={formData.detailedDescription} 
                      onChange={handleInputChange} 
                      rows="4"
                      placeholder="Comprehensive course description"
                      required 
                    />
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>Price (₹)</label>
                      <input 
                        type="number" 
                        name="price" 
                        value={formData.price} 
                        onChange={handleInputChange} 
                        placeholder="0 for free"
                      />
                    </div>

                    <div className="form-group">
                      <label>Display Order</label>
                      <input 
                        type="number" 
                        name="order" 
                        value={formData.order} 
                        onChange={handleInputChange} 
                        placeholder="Higher number = higher priority"
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} />
                      <span>Active</span>
                    </label>
                    <small className="form-hint">Inactive courses won't be visible to users</small>
                  </div>
                </div>
              )}

              {activeTab === 'syllabus' && (
                <div className="tab-content">
                  {renderSyllabusEditor()}
                </div>
              )}

              {activeTab === 'features' && (
                <div className="tab-content">
                  <div className="form-group full-width">
                    <label>Course Features (comma separated) *</label>
                    <textarea 
                      value={featuresInput} 
                      onChange={handleFeaturesChange} 
                      placeholder="Machine Learning Algorithms, Deep Learning, Natural Language Processing, etc."
                      rows="4"
                    />
                    <small className="form-hint">Separate features with commas</small>
                  </div>

                  <div className="form-group full-width">
                    <label>Course Image</label>
                    <div className="image-upload-area">
                      {imagePreview ? (
                        <div className="image-preview">
                          <img src={imagePreview} alt="Preview" />
                          <button 
                            type="button" 
                            className="remove-image"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview('');
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <path d="M21 15L16 10L5 21"/>
                          </svg>
                          <p>Click or drag to upload image</p>
                          <small>PNG, JPG, JPEG up to 5MB</small>
                        </div>
                      )}
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange}
                        className="image-input"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className="image-upload-label">
                        {imagePreview ? 'Change Image' : 'Choose Image'}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingCourse ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesManager;